import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { UPDATE_USER_BY_ADMIN } from "redux/features/superAdminSlice/manageUsersSlice/manageUsersGql";
import { useUploadImage } from "redux/hooks";
import { FileUpload } from "shared/Components/fileUpload";
import { PhoneInput } from "shared/Components/phoneInput";
import { TextInput } from "shared/Components/textInput";
import { TextArea } from "shared/Components/textarea";
import { registerMemberResolver } from "validators/UserValidation";
import { globalVariables } from "helper/globalVariables";
import { SearchLocation } from "shared/Components/searchLocation";

export const MemberProfile = forwardRef((props, ref) => {
  const { userProfileData, onProfileSuccess, onProfileError, setLoaderState } =
    props || {};
  const { onUploadImage, imageUrl } = useUploadImage();
  const [memberImage] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [checkValidation, setCheckValidation] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    setCityFilter(userProfileData?.city);
    setStateFilter(userProfileData?.state);
  }, []);

  const [updateProfile] = useMutation(UPDATE_USER_BY_ADMIN, {
    variables: {
      authId: id,
      data: {},
    },
  });

  useImperativeHandle(ref, () => ({
    saveChanges() {
      onCheckValidation();
    },
  }));

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: registerMemberResolver,
    defaultValues: {
      firstName: userProfileData?.name.split(" ")[0],
      lastName: userProfileData?.name.split(" ")[1],
      email: userProfileData?.email,
      location: userProfileData?.location_address,
      phoneNumber: userProfileData?.phone_number,
      emergencyContactName: userProfileData?.emergency_contact_name,
      emergencyContactNumber: userProfileData?.emergency_contact_number,
      zip: userProfileData?.zipCode,
    },
  });

  const onCheckValidation = () => {
    if (!cityFilter || !stateFilter) {
      setCheckValidation(true);
      const missing = [];
      if (!stateFilter) missing.push("State");
      if (!cityFilter) missing.push("City");
      onProfileError?.(`Please fill in the required field(s): ${missing.join(", ")}.`);
      return;
    }

    handleSubmit(onHandleSubmit)();
  };

  const onValidate = () => {
    if (cityFilter && stateFilter) {
      setCheckValidation(false);
    } else {
      setCheckValidation(true);
    }
  };

  const onHandleSubmit = async (credentials) => {
    try {
      setLoaderState(true);
      const { data } = await updateProfile({
        variables: {
          authId: id,
          data: {
            first_name: credentials.firstName,
            last_name: credentials.lastName,
            phone_number: credentials.phoneNumber,
            emergency_contact_name: credentials.emergencyContactName,
            emergency_contact_number: credentials.emergencyContactNumber,
            location_address: credentials.location,
            profile: imageUrl,
            zipCode: credentials?.zip,
            city: cityFilter,
            state: stateFilter,
          },
        },
      });
      onProfileSuccess();
      console.log(data.updateUserByAdmin.message); // Handle the response as needed
    } catch (error) {
      console.error("Error updating user:", error);
      onProfileError();
    } finally {
      setLoaderState(false);
    }
  };

  return (
    <div style={{ paddingBottom: "100px" }}>
      <div className="w-full flex justify-center align-center">
        <span className="text-2xl md:text-3xl font-semibold text-gray-900 text-center mb-6">
          {globalVariables.userDetails}
        </span>
      </div>
      <FileUpload
        onSetImage={(img) => onUploadImage(img)}
        defaultImage={userProfileData?.profile || memberImage}
      />
      <div className="mt-6" />
      <form
        className="flex justify-center pb-[24px]"
        onSubmit={handleSubmit(onHandleSubmit)}
        ref={ref}
      >
        <div className="max-[400px]:w-[90%]">
          <TextInput
            register={register}
            name="firstName"
            placeholder={globalVariables.enterFirstName}
            errorMsg={errors.firstName?.message}
            label={globalVariables.firstName}
          />
          <div className="mt-5" />
          <TextInput
            register={register}
            name="lastName"
            placeholder={globalVariables.enterLastName}
            errorMsg={errors.lastName?.message}
            label={globalVariables.lastName}
          />
          <div className="mt-5" />
          <TextInput
            register={register}
            name="email"
            placeholder={globalVariables.enterEmailAddress}
            errorMsg={errors.email?.message}
            disabled
            label={globalVariables.emailAddress}
            textTransform="normal-case"
          />
          <div className="mt-5" />
          <TextArea
            register={register}
            name="location"
            placeholder={globalVariables.enterLocationAddress}
            errorMsg={errors.location?.message}
            label={globalVariables.address}
          />
          <div className="mt-5" />
          <SearchLocation
            label={globalVariables.city}
            width={"w-[358px] lg:w-[384px] max-[400px]:w-[100%]"}
            name="city"
            placeholder={globalVariables.city}
            types={["(cities)"]}
            onSelectLocation={(location) => {
              setCityFilter(location);
              onValidate();
            }}
            errorMsg={
              checkValidation && !cityFilter && globalVariables.pleaseSelectCity
            }
            defaultValue={cityFilter}
            searchBy={stateFilter}
          />
          <div className="mt-5" />
          <SearchLocation
            label={globalVariables.state}
            width={"w-[358px] lg:w-[384px] max-[400px]:w-[100%]"}
            name="State"
            types={["(regions)"]}
            placeholder={globalVariables.state}
            onSelectLocation={(location) => {
              setStateFilter(location);
              onValidate();
            }}
            errorMsg={
              checkValidation &&
              !stateFilter &&
              globalVariables.pleaseSelectState
            }
            defaultValue={stateFilter}
          />

          <div className="mt-5" />
          <TextInput
            register={register}
            name="zip"
            placeholder={globalVariables.zip}
            label={globalVariables.zip}
            errorMsg={errors.zip?.message}
            type="number"
          />
          <div className="mt-5" />
          <PhoneInput
            name="phoneNumber"
            placeholder={globalVariables.enterPhoneNumber}
            errorMsg={errors.phoneNumber?.message}
            label={globalVariables.phoneNumber}
            value={watch("phoneNumber")}
            onChange={(e) => setValue("phoneNumber", e.target.value)}
          />
          <div className="mt-5" />
          <TextInput
            register={register}
            name="emergencyContactName"
            placeholder={globalVariables.enterEmergencyContactName}
            errorMsg={errors.emergencyContactName?.message}
            label={globalVariables.emergencyContactName}
          />
          <div className="mt-5" />
          <PhoneInput
            name="emergencyContactNumber"
            placeholder={globalVariables.enterEmergencyPhoneNumber}
            errorMsg={errors.emergencyContactNumber?.message}
            label={globalVariables.emergencyPhoneNumber}
            value={watch("emergencyContactNumber")}
            onChange={(e) => setValue("emergencyContactNumber", e.target.value)}
          />
        </div>
      </form>
    </div>
  );
});
